// ============================================================================
// stage1-import-completion.ts: what a Stage 1 drop does with the documents it
// imports, for a job whose files arrived another way (Import Projects / the
// Quote Run). Peter, 2026-10-23: "For Processing and quote generation, Import
// Projects/Quote Run needs to behave the same as a Stage 1 drag-and-drop run."
//
// A Stage 1 drop classifies the documents with the AI, picks the request text
// it reads the client and job from, writes that text to the RFQ box and lifts
// a client's own Scope of Works onto the form. A folder import did none of it,
// so the Work Method, Stage 4 seed and quote prompts went without the RFQ text
// and the client's scope. The selection rules live here, used by both routes.
// ============================================================================

import { classifyPlainTextFileForRouting } from '../utils/stage1-prompts-helpers';
import { stage1EmailCleanBody } from './stage1-email-clean-body';
import { stage1TextFileRoute, type Stage1TextFileRoute } from './stage1-estimator-notes';
import { applyStage1ImportRfqText, stage1ImportRfqText } from './stage1-import-rfq-text';
import { buildStage1PreMadeScopeDoc, selectStage1PreMadeScopeDocument } from './stage1-premade-scope';
import { buildStage1CombinedExtractionText } from './stage1-rfq-extraction';

type Doc = Record<string, any>;
export interface ExtractionPart { content: string; name: string; isEmail: boolean }

/** A combined text above this is not used; the single best document is read instead (Stage 1's rule). */
const MAX_COMBINED_EXTRACTION_CHARS = 12000;

function documentText(document: Doc | null | undefined): string {
    const value = document?.content ?? document?.text ?? '';
    return typeof value === 'string' ? value : '';
}

function extension(document: Doc | null | undefined): string {
    const name = String(document?.name || document?.originalName || '');
    const dot = name.lastIndexOf('.');
    return dot >= 0 ? name.slice(dot + 1).toLowerCase() : '';
}

/** An email document from either route: Stage 1 flags it, a folder import keeps its headers in metadata. */
export function isStage1EmailDocument(document: Doc | null | undefined): boolean {
    return document?.isEmail === true
        || ['eml', 'msg'].includes(extension(document))
        || !!(document?.metadata?.emailFrom || document?.metadata?.emailSubject);
}

/**
 * The email's own words, as Stage 1 keeps them (`cleanBody`). A folder import
 * stores the header block and the body together, so the header lines are
 * dropped before the same cleaning.
 */
export function stage1EmailCleanBodyOf(document: Doc | null | undefined): string {
    if (typeof document?.cleanBody === 'string') return document.cleanBody;
    const text = documentText(document);
    const headerBlock = text.match(/^(?:(?:From|To|CC|Cc|Date|Subject):[^\n]*\n)+\n/);
    return stage1EmailCleanBody(headerBlock ? text.slice(headerBlock[0].length) : text);
}

/** The text Stage 1 counts as readable: an error placeholder or a scrap is not. */
function readableDocumentText(document: Doc): string {
    const text = documentText(document);
    return text.length > 30 && !text.startsWith('[PDF ') && !text.startsWith('[Could not') ? text : '';
}

/**
 * Stage 1's per-file pick, in import order, before the AI classification: a
 * request outranks other documents, and an email takes over only from nothing,
 * from another email, or when it reads as the request (CN2).
 */
export function stage1InitialExtractionSource(documents: Doc[], { rfqPriorityDisabled = false } = {}) {
    let contentForExtraction = '';
    let fileNameForExtraction = '';
    let contentIsEmail = false;
    let latestEmailCleanBody = '';
    let latestEmailDocumentId = '';
    const allContent: ExtractionPart[] = [];
    for (const document of Array.isArray(documents) ? documents : []) {
        if (!document || typeof document !== 'object') continue;
        const name = String(document.name || '');
        if (isStage1EmailDocument(document)) {
            const text = documentText(document);
            allContent.push({ content: text, name, isEmail: true });
            if (rfqPriorityDisabled || !contentForExtraction || contentIsEmail || document.type === 'rfq') {
                contentForExtraction = text;
                fileNameForExtraction = name;
                contentIsEmail = true;
            }
            latestEmailCleanBody = stage1EmailCleanBodyOf(document);
            latestEmailDocumentId = String(document.id || '');
            continue;
        }
        const text = readableDocumentText(document);
        if (!text) continue;
        allContent.push({ content: text, name, isEmail: false });
        if (document.type === 'rfq' || !contentForExtraction) {
            contentForExtraction = text;
            fileNameForExtraction = name;
            contentIsEmail = false;
        }
    }
    return { contentForExtraction, fileNameForExtraction, allContent, latestEmailCleanBody, latestEmailDocumentId };
}

/**
 * After the AI classification, Stage 1 reads the document classified as the
 * request (a non-email one first). A non-email request is read on its own, so
 * inspector emails never lend it their sender as the client.
 */
export function stage1RfqExtractionPick(documents: Doc[], { rfqPriorityDisabled = false } = {}) {
    const usable = (document: Doc) => {
        const text = documentText(document);
        return text.length > 30 && !text.startsWith('[');
    };
    const list = Array.isArray(documents) ? documents : [];
    const rfqDocument = list.find(d => d?.type === 'rfq' && !isStage1EmailDocument(d) && usable(d))
        || list.find(d => d?.type === 'rfq' && usable(d))
        || null;
    if (!rfqDocument) return { rfqDocument: null, allContent: null, isolated: false };
    return {
        rfqDocument,
        allContent: list.filter(d => d && usable(d))
            .map(d => ({ content: documentText(d), name: String(d.name || ''), isEmail: isStage1EmailDocument(d) })),
        isolated: !isStage1EmailDocument(rfqDocument) && !rfqPriorityDisabled,
    };
}

/** Several readable documents are read together, unless the request was isolated or the result is too long. */
export function combineStage1ExtractionContent({ allContent, contentForExtraction, isolated }: {
    allContent: ExtractionPart[]; contentForExtraction: string; isolated: boolean;
}): { content: string; fileName: string; label: string } | null {
    if (allContent.length <= 1 || !contentForExtraction || isolated) return null;
    // CN5: each part is headed by its position and format, never its file name.
    const combined = buildStage1CombinedExtractionText(allContent);
    if (combined.length > MAX_COMBINED_EXTRACTION_CHARS) return null;
    return { content: combined, fileName: allContent.map(part => part.name).join(', '), label: `${allContent.length} documents` };
}

/** The whole Stage 1 choice for a set of documents that are already classified. */
export function selectStage1ExtractionSource(documents: Doc[], options: { rfqPriorityDisabled?: boolean } = {}) {
    const initial = stage1InitialExtractionSource(documents, options);
    let { contentForExtraction, fileNameForExtraction, allContent } = initial;
    let label = '';
    const pick = stage1RfqExtractionPick(documents, options);
    if (pick.rfqDocument) {
        contentForExtraction = documentText(pick.rfqDocument);
        fileNameForExtraction = String(pick.rfqDocument.name || '');
        allContent = pick.allContent || allContent;
    }
    const combined = combineStage1ExtractionContent({ allContent, contentForExtraction, isolated: pick.isolated });
    if (combined) {
        contentForExtraction = combined.content;
        fileNameForExtraction = combined.fileName;
        label = combined.label;
    }
    return {
        contentForExtraction,
        fileNameForExtraction,
        documentLabelForExtraction: label,
        latestEmailCleanBody: initial.latestEmailCleanBody,
        latestEmailDocumentId: initial.latestEmailDocumentId,
        rfqDocument: pick.rfqDocument,
        isolated: pick.isolated,
    };
}

/**
 * The form fields a Full AI Stage 1 import writes from its documents, for a job
 * whose files skipped that import: the RFQ text (never asking, as a Full AI
 * import never asks) and the client's own Scope of Works. A field that already
 * has a value is left alone, so an RFQ the operator chose or edited stays.
 */
export function completeStage1ImportForm<F extends Doc>(formData: F, documents: unknown[], {
    rfqPriorityDisabled = false, now = new Date(),
}: { rfqPriorityDisabled?: boolean; now?: Date } = {}): F & Doc {
    let next: Doc = formData || {};
    const list = (Array.isArray(documents) ? documents : []) as Doc[];
    if (list.length === 0) return next as F & Doc;
    if (!String(next.rfqContent || '').trim()) {
        const source = selectStage1ExtractionSource(list, { rfqPriorityDisabled });
        const rfqText = stage1ImportRfqText({
            latestEmailCleanBody: source.latestEmailCleanBody,
            latestEmailDocument: list.find(doc => String(doc?.id || '') === source.latestEmailDocumentId) || null,
            contentForExtraction: source.contentForExtraction,
            attended: false,
        });
        if (rfqText) {
            next = applyStage1ImportRfqText(next, { rfqText, documents: list, decidedAt: now.toISOString() }).formData;
        }
    }
    if (!next.preMadeScopeDoc) {
        const scopeDocument = selectStage1PreMadeScopeDocument(list);
        if (scopeDocument) next = { ...next, preMadeScopeDoc: buildStage1PreMadeScopeDoc(scopeDocument, now) };
    }
    return next as F & Doc;
}

// The plain text files the File Explorer and a Stage 1 drop both accept.
const PLAIN_TEXT_EXTENSIONS: ReadonlySet<string> = new Set(['txt', 'md', 'text']);

/**
 * Where a Stage 1 drop sends a plain text file (its text step, by the same
 * classifyPlainTextFileForRouting): a request or scope is read as a document,
 * the business's estimator notes are kept with their prices removed, and every
 * other text goes into Inspector Notes. Not a plain text file: null.
 */
export function stage1TextDocumentRoute(document: unknown): Stage1TextFileRoute | null {
    const doc = document as Doc | null | undefined;
    if (!PLAIN_TEXT_EXTENSIONS.has(extension(doc))) return null;
    const text = documentText(doc);
    if (!text.trim()) return null;
    const route = classifyPlainTextFileForRouting(String(doc?.name || ''), text);
    return route.shouldRouteAsDocument ? 'document' : stage1TextFileRoute(route.classification);
}

/**
 * A document a folder import brought in (Import Projects, the Quote Run). Only
 * that import's preprocessing stamps `preprocessedAt` (preprocessing-runner.ts);
 * a Stage 1 drop never does, so a job built in Stage 1 is never touched here.
 */
export function isFolderImportedDocument(document: unknown): boolean {
    return !!(document as Doc | null | undefined)?.preprocessedAt;
}
